import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { Divider } from 'primereact/divider';
import { classNames } from 'primereact/utils';
import authService from '../../services/auth.service';
import oauthService from '../../services/oauth.service';
import OTPVerificationModal from '../../components/auth/OTPVerificationModal';
import axiosInstance from '../../config/axios.config';

const RegisterV2 = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    
    // Form state
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        password_confirmation: '',
        mobile_number: '',
        acceptTerms: false
    });
    
    // UI state
    const [loading, setLoading] = useState(false);
    const [oauthLoading, setOauthLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    
    // OTP state
    const [emailVerified, setEmailVerified] = useState(false);
    const [verifiedOtp, setVerifiedOtp] = useState('');
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [sendingOtp, setSendingOtp] = useState(false);
    
    // Clear errors when component mounts
    useEffect(() => {
        setErrors({});
        setSubmitted(false);
    }, []);
    
    // Handle input change
    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'acceptTerms' ? checked : value
        }));
        
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };
    
    // Validate form
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.first_name.trim()) {
            newErrors.first_name = 'First name is required';
        }
        
        if (!formData.last_name.trim()) {
            newErrors.last_name = 'Last name is required';
        }
        
        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }
        
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }
        
        if (!formData.password_confirmation) {
            newErrors.password_confirmation = 'Please confirm your password';
        } else if (formData.password !== formData.password_confirmation) {
            newErrors.password_confirmation = 'Passwords do not match';
        }
        
        if (!formData.acceptTerms) {
            newErrors.acceptTerms = 'You must accept the terms and conditions';
        }
        
        // Mobile number validation (optional but if provided, must be valid)
        if (formData.mobile_number && !/^[0-9]{10}$/.test(formData.mobile_number.replace(/[^0-9]/g, ''))) {
            newErrors.mobile_number = 'Please enter a valid 10-digit mobile number';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    // Handle sending OTP
    const handleSendOTP = async () => {
        // Basic email validation
        if (!formData.email) {
            setErrors({ ...errors, email: 'Please enter your email first' });
            return;
        }
        
        if (!/\S+@\S+\.\S+/.test(formData.email)) {
            setErrors({ ...errors, email: 'Please enter a valid email' });
            return;
        }
        
        setSendingOtp(true);
        setErrors({});
        
        try {
            const response = await axiosInstance.post('/auth/send-otp', {
                email: formData.email
            });
            
            if (response.data.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'OTP Sent',
                    detail: 'Verification code sent to your email',
                    life: 3000
                });
                setShowOtpModal(true);
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to send OTP';
            
            // If email already registered, show both field error and toast
            if (message.includes('already registered')) {
                setErrors({ email: message });
                toast.current.show({
                    severity: 'warn',
                    summary: 'Email Already Registered',
                    detail: message,
                    life: 6000
                });
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: message,
                    life: 5000
                });
            }
        } finally {
            setSendingOtp(false);
        }
    };
    
    // Handle OTP verification success
    const handleOtpVerified = (otp) => {
        setVerifiedOtp(otp);
        setEmailVerified(true);
        toast.current.show({
            severity: 'success',
            summary: 'Email Verified',
            detail: 'Your email has been verified successfully!',
            life: 3000
        });
    };
    
    // Handle OTP resend
    const handleOtpResend = () => {
        toast.current.show({
            severity: 'info',
            summary: 'OTP Resent',
            detail: 'A new verification code has been sent to your email',
            life: 3000
        });
    };
    
    // Handle traditional registration
    const handleRegister = async (e) => {
        e.preventDefault();
        setSubmitted(true);
        
        if (!validateForm()) {
            return;
        }
        
        if (!emailVerified) {
            toast.current.show({
                severity: 'error',
                summary: 'Email Not Verified',
                detail: 'Please verify your email before registering',
                life: 5000
            });
            return;
        }
        
        setLoading(true);
        
        try {
            // Include OTP in registration data
            const registrationData = {
                ...formData,
                otp: verifiedOtp
            };
            
            const result = await authService.registerV2(registrationData);
            
            if (result.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Registration Successful',
                    detail: 'Your account has been created successfully!',
                    life: 3000
                });
                
                // Redirect to dashboard after short delay
                setTimeout(() => {
                    navigate('/app/dashboard');
                }, 1500);
            } else {
                // Handle validation errors from server
                if (result.errors) {
                    setErrors(result.errors);
                }
                
                toast.current.show({
                    severity: 'error',
                    summary: 'Registration Failed',
                    detail: result.message || 'Failed to create account',
                    life: 5000
                });
            }
        } catch (error) {
            console.error('Registration error:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'An unexpected error occurred. Please try again.',
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };
    
    // Handle Google OAuth registration
    const handleGoogleRegister = async () => {
        setOauthLoading(true);
        
        try {
            const result = await oauthService.initiateGoogleOAuth(formData.email || null);
            
            if (result.success) {
                // Redirect to Google OAuth
                window.location.href = result.oauth_url;
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'OAuth Failed',
                    detail: result.message || 'Failed to initiate Google authentication',
                    life: 5000
                });
            }
        } catch (error) {
            console.error('OAuth error:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to connect with Google. Please try again.',
                life: 5000
            });
        } finally {
            setOauthLoading(false);
        }
    };
    
    const getFormErrorMessage = (name) => {
        return errors[name] && submitted && <small className="p-error">{errors[name]}</small>;
    };
    
    // Password strength indicator
    const passwordHeader = <div className="font-bold mb-3">Pick a password</div>;
    const passwordFooter = (
        <>
            <Divider />
            <p className="mt-2">Suggestions:</p>
            <ul className="pl-2 ml-2 mt-0 line-height-3">
                <li>At least one lowercase</li>
                <li>At least one uppercase</li>
                <li>At least one numeric</li>
                <li>Minimum 8 characters</li>
            </ul>
        </>
    );
    
    return (
        <div className="flex align-items-center justify-content-center min-h-screen min-w-screen overflow-hidden">
            <Toast ref={toast} />
            
            <div className="flex flex-column align-items-center justify-content-center">
                <div style={{ 
                    borderRadius: '56px', 
                    padding: '0.3rem', 
                    background: 'linear-gradient(180deg, var(--primary-color) 10%, rgba(33, 150, 243, 0) 30%)' 
                }}>
                    <div className="w-full surface-card py-8 px-5 sm:px-8" style={{ borderRadius: '53px' }}>
                        {/* Logo/Title Section */}
                        <div className="text-center mb-5">
                            <h1 className="text-900 text-3xl font-medium mb-3">Create Account</h1>
                            <span className="text-600 font-medium">Join us today!</span>
                        </div>
                        
                        {/* Google OAuth Button */}
                        <Button 
                            label="Sign up with Google"
                            icon="pi pi-google"
                            className="w-full p-3 text-xl mb-4"
                            severity="secondary"
                            outlined
                            onClick={handleGoogleRegister}
                            loading={oauthLoading}
                            disabled={loading}
                        />
                        
                        {/* Divider */}
                        <Divider align="center" className="mb-4">
                            <span className="text-600 font-normal text-sm">OR</span>
                        </Divider>
                        
                        <form onSubmit={handleRegister}>
                            {/* Name Fields Row */}
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <div className="field mb-4">
                                        <label htmlFor="first_name" className="block text-900 font-medium mb-2">
                                            First Name <span className="text-red-500">*</span>
                                        </label>
                                        <InputText
                                            id="first_name"
                                            name="first_name"
                                            type="text"
                                            placeholder="Enter your first name"
                                            className={classNames('w-full', { 'p-invalid': errors.first_name && submitted })}
                                            value={formData.first_name}
                                            onChange={handleChange}
                                            disabled={loading || oauthLoading}
                                            autoComplete="given-name"
                                        />
                                        {getFormErrorMessage('first_name')}
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="field mb-4">
                                        <label htmlFor="last_name" className="block text-900 font-medium mb-2">
                                            Last Name <span className="text-red-500">*</span>
                                        </label>
                                        <InputText
                                            id="last_name"
                                            name="last_name"
                                            type="text"
                                            placeholder="Enter your last name"
                                            className={classNames('w-full', { 'p-invalid': errors.last_name && submitted })}
                                            value={formData.last_name}
                                            onChange={handleChange}
                                            disabled={loading || oauthLoading}
                                            autoComplete="family-name"
                                        />
                                        {getFormErrorMessage('last_name')}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Email Field with Verification */}
                            <div className="field mb-4">
                                <label htmlFor="email" className="block text-900 font-medium mb-2">
                                    Email <span className="text-red-500">*</span>
                                    {emailVerified && <span className="text-green-500 ml-2">✓ Verified</span>}
                                </label>
                                <div className="p-inputgroup">
                                    <InputText
                                        id="email"
                                        name="email"
                                        type="email"
                                        placeholder="Email address"
                                        className={classNames('', { 'p-invalid': errors.email && submitted })}
                                        value={formData.email}
                                        onChange={(e) => {
                                            handleChange(e);
                                            // Reset verification if email changes
                                            if (emailVerified) {
                                                setEmailVerified(false);
                                                setVerifiedOtp('');
                                            }
                                        }}
                                        disabled={loading || oauthLoading || emailVerified}
                                        autoComplete="email"
                                    />
                                    <Button 
                                        type="button"
                                        label={emailVerified ? "Verified ✓" : "Verify"}
                                        onClick={handleSendOTP}
                                        disabled={emailVerified || !formData.email || sendingOtp || loading}
                                        loading={sendingOtp}
                                        className={emailVerified ? "p-button-success" : ""}
                                        style={{ minWidth: '100px' }}
                                    />
                                </div>
                                {getFormErrorMessage('email')}
                            </div>
                            
                            {/* Mobile Number Field */}
                            <div className="field mb-4">
                                <label htmlFor="mobile_number" className="block text-900 font-medium mb-2">
                                    Mobile Number
                                </label>
                                <InputText
                                    id="mobile_number"
                                    name="mobile_number"
                                    type="tel"
                                    placeholder="10-digit mobile number"
                                    className={classNames('w-full', { 'p-invalid': errors.mobile_number && submitted })}
                                    value={formData.mobile_number}
                                    onChange={handleChange}
                                    disabled={loading || oauthLoading}
                                    autoComplete="tel"
                                />
                                {getFormErrorMessage('mobile_number')}
                            </div>
                            
                            {/* Password Fields */}
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <div className="field mb-4">
                                        <label htmlFor="password" className="block text-900 font-medium mb-2">
                                            Password <span className="text-red-500">*</span>
                                        </label>
                                        <Password
                                            id="password"
                                            name="password"
                                            placeholder="Password"
                                            toggleMask
                                            className={classNames('w-full', { 'p-invalid': errors.password && submitted })}
                                            inputClassName="w-full"
                                            value={formData.password}
                                            onChange={handleChange}
                                            disabled={loading || oauthLoading}
                                            header={passwordHeader}
                                            footer={passwordFooter}
                                            inputProps={{ autoComplete: 'new-password' }}
                                        />
                                        {getFormErrorMessage('password')}
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="field mb-4">
                                        <label htmlFor="password_confirmation" className="block text-900 font-medium mb-2">
                                            Confirm Password <span className="text-red-500">*</span>
                                        </label>
                                        <Password
                                            id="password_confirmation"
                                            name="password_confirmation"
                                            placeholder="Confirm password"
                                            toggleMask
                                            className={classNames('w-full', { 'p-invalid': errors.password_confirmation && submitted })}
                                            inputClassName="w-full"
                                            value={formData.password_confirmation}
                                            onChange={handleChange}
                                            disabled={loading || oauthLoading}
                                            feedback={false}
                                            inputProps={{ autoComplete: 'new-password' }}
                                        />
                                        {getFormErrorMessage('password_confirmation')}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Terms and Conditions */}
                            <div className="field mb-5">
                                <div className="flex align-items-center">
                                    <Checkbox 
                                        id="acceptTerms" 
                                        name="acceptTerms"
                                        checked={formData.acceptTerms} 
                                        onChange={handleChange}
                                        className={classNames('mr-2', { 'p-invalid': errors.acceptTerms && submitted })}
                                        disabled={loading || oauthLoading}
                                    />
                                    <label htmlFor="acceptTerms" className="text-900">
                                        I agree to the <Link to="/terms" className="font-medium no-underline" style={{ color: 'var(--primary-color)' }}>Terms and Conditions</Link>
                                    </label>
                                </div>
                                {getFormErrorMessage('acceptTerms')}
                            </div>
                            
                            {/* Register Button */}
                            <Button 
                                label="Create Account" 
                                type="submit"
                                className="w-full p-3 text-xl" 
                                loading={loading}
                                disabled={!emailVerified || loading || oauthLoading || !formData.acceptTerms}
                            />
                        </form>
                        
                        {/* Login Link */}
                        <div className="mt-5 text-center">
                            <span className="text-600 font-medium">Already have an account? </span>
                            <Link to="/app/auth/login-v2" className="font-medium no-underline ml-2" style={{ color: 'var(--primary-color)' }}>
                                Sign in
                            </Link>
                        </div>

                        {/* Multi-user Registration Link */}
                        <div className="mt-3 pt-3 border-top-1 surface-border text-center">
                            <i className="pi pi-building mr-2 text-600"></i>
                            <span className="text-600 font-medium">Registering as company? </span>
                            <Link to="/app/register/company" className="font-medium no-underline ml-2" style={{ color: 'var(--primary-color)' }}>
                                Multi-user Registration
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* OTP Verification Modal */}
            <OTPVerificationModal
                visible={showOtpModal}
                onHide={() => setShowOtpModal(false)}
                email={formData.email}
                onVerified={handleOtpVerified}
                onResend={handleOtpResend}
            />
        </div>
    );
};

export default RegisterV2;